"use client";

import { Fragment } from "react";
import classNames from "classnames/dedupe";
import type { PaymentTermsOption, AgreementsContent } from "./types";

/** Parsed segment of a label. */
type LabelSegment = { type: "text" | "link"; content: string; title?: string };

type SegmentWithAgreement = LabelSegment & { agreement?: AgreementsContent };

export type CheckboxItemProps = {
  id: string;
  option: PaymentTermsOption;
  isChecked: boolean;
  onCheckboxChange: (value: string, checked: boolean) => void;
  onLinkClick: (agreements: AgreementsContent[]) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
};

const parseLabel = (label: string): LabelSegment[] => {
  const segments: LabelSegment[] = [];
  const anchorRegex = /<a[^>]*>([^<]*)<\/a>/gi;
  let lastIndex = 0;
  let match;

  while ((match = anchorRegex.exec(label)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: label.slice(lastIndex, match.index),
      });
    }
    const titleMatch = match[0].match(/title=["']([^"']*)["']/i);
    segments.push({ type: "link", content: match[1], title: titleMatch?.[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < label.length) {
    segments.push({ type: "text", content: label.slice(lastIndex) });
  }

  return segments;
};

export const CheckboxItem = ({
  id,
  option,
  isChecked,
  onCheckboxChange,
  onLinkClick,
  isDisabled,
  isInvalid,
}: CheckboxItemProps) => {
  const { agreements } = option;
  const modifiers = {
    "--is-disabled": isDisabled,
    "--is-selected": isChecked,
    "--is-invalid": isInvalid && !isChecked,
  };

  let linkIdx = 0;
  const segmentsWithAgreements: SegmentWithAgreement[] = parseLabel(
    option.label,
  ).map(segment => ({
    ...segment,
    agreement: segment.type === "link" ? agreements?.[linkIdx++] : undefined,
  }));

  const handleLinkActivation = (
    e: React.SyntheticEvent,
    agreements: AgreementsContent[],
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      onLinkClick(agreements);
    }
  };

  return (
    <label
      htmlFor={id}
      className={classNames("mobius-payment-terms-checklist__label", modifiers)}
    >
      <input
        id={id}
        type="checkbox"
        className={classNames(
          "mobius-payment-terms-checklist__input",
          modifiers,
        )}
        checked={isChecked}
        disabled={isDisabled}
        onChange={e => onCheckboxChange(option.value, e.target.checked)}
      />
      <span className="mobius-payment-terms-checklist__text">
        {segmentsWithAgreements.map((segment, i) => {
          if (segment.type === "link" && segment.agreement && agreements) {
            return (
              <span
                key={i}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isDisabled}
                aria-label={segment.title ?? ""}
                className="mobius-payment-terms-checklist__link"
                onClick={e => handleLinkActivation(e, agreements)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleLinkActivation(e, agreements);
                  }
                }}
                title={segment.title}
              >
                {segment.content}
              </span>
            );
          }
          return <Fragment key={i}>{segment.content}</Fragment>;
        })}
      </span>
    </label>
  );
};
